import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { adminFhirIntegrationEntryEndpoints, type FhirIntegrationDto } from './integrationEndpoints';

/** React Query key for the FHIR-integration entry list (feature-local; not shared). */
const FHIR_INTEGRATION_ENTRIES_KEY = ['fhir-integrations', 'entries'] as const;

/** The two token-acquisition strategies carried by an entry (mirrors the backend enum). */
export const AUTHORIZATION_CRITERIA_OPTIONS = ['REFRESH_TOKEN', 'SYSTEM_ACCOUNT'] as const;
export type AuthorizationCriteria = (typeof AUTHORIZATION_CRITERIA_OPTIONS)[number];

function normaliseCriteria(value: string | null | undefined): AuthorizationCriteria {
  return value === 'SYSTEM_ACCOUNT' ? 'SYSTEM_ACCOUNT' : 'REFRESH_TOKEN';
}

/** Normalised view model — the wire DTO is all-optional; the UI wants non-null. */
export interface FhirIntegrationViewModel {
  readonly id: string;
  readonly name: string;
  readonly isDefault: boolean;
  readonly active: boolean;
  readonly skipAuthentication: boolean;
  readonly authorizationCriteria: AuthorizationCriteria;
  readonly patientIdHeaderName: string;
  readonly accessTokenHeaderName: string;
  readonly frontendBaseUrl: string;
  readonly patientIdSearchCriteria: string;
  readonly patientIdExtensionUrl: string;
  readonly fhirServiceRootUrl: string;
  readonly hasSystemAccountUser: boolean;
  readonly hasSystemAccountPass: boolean;
  readonly auth0TokenUrl: string;
  readonly auth0ApiAudience: string;
  readonly auth0Connection: string;
  readonly hasAuth0ClientId: boolean;
  readonly hasAuth0ClientSecret: boolean;
  readonly authorizationHost: string;
  readonly hasSmartClientId: boolean;
  readonly hasSmartClientSecret: boolean;
  readonly smartRedirectUri: string;
  readonly smartScopes: string;
}

export function mapFhirIntegration(dto: FhirIntegrationDto): FhirIntegrationViewModel {
  return {
    id: dto.id ?? '',
    name: dto.name ?? '',
    isDefault: dto.is_default ?? false,
    active: dto.active ?? true,
    skipAuthentication: dto.skip_authentication ?? false,
    authorizationCriteria: normaliseCriteria(dto.authorization_criteria),
    patientIdHeaderName: dto.patient_id_header_name ?? '',
    accessTokenHeaderName: dto.access_token_header_name ?? '',
    frontendBaseUrl: dto.frontend_base_url ?? '',
    patientIdSearchCriteria: dto.patient_id_search_criteria ?? '',
    patientIdExtensionUrl: dto.patient_id_extension_url ?? '',
    fhirServiceRootUrl: dto.fhir_service_root_url ?? '',
    hasSystemAccountUser: dto.has_system_account_user ?? false,
    hasSystemAccountPass: dto.has_system_account_pass ?? false,
    auth0TokenUrl: dto.auth0_token_url ?? '',
    auth0ApiAudience: dto.auth0_api_audience ?? '',
    auth0Connection: dto.auth0_connection ?? '',
    hasAuth0ClientId: dto.has_auth0_client_id ?? false,
    hasAuth0ClientSecret: dto.has_auth0_client_secret ?? false,
    authorizationHost: dto.authorization_host ?? '',
    hasSmartClientId: dto.has_smart_client_id ?? false,
    hasSmartClientSecret: dto.has_smart_client_secret ?? false,
    smartRedirectUri: dto.smart_redirect_uri ?? '',
    smartScopes: dto.smart_scopes ?? '',
  };
}

export interface FhirIntegrationInput {
  readonly name: string;
  readonly isDefault: boolean;
  readonly active: boolean;
  readonly skipAuthentication: boolean;
  readonly authorizationCriteria: AuthorizationCriteria;
  readonly patientIdHeaderName: string;
  readonly accessTokenHeaderName: string;
  readonly frontendBaseUrl: string;
  readonly patientIdSearchCriteria: string;
  readonly patientIdExtensionUrl: string;
  readonly fhirServiceRootUrl: string;
  /** Plaintext; empty on update means "keep the stored value". */
  readonly systemAccountUser: string;
  /** Plaintext; empty on update means "keep the stored value". */
  readonly systemAccountPass: string;
  readonly auth0TokenUrl: string;
  readonly auth0ApiAudience: string;
  readonly auth0Connection: string;
  /** Plaintext; empty on update means "keep the stored value". */
  readonly auth0ClientId: string;
  /** Plaintext; empty on update means "keep the stored value". */
  readonly auth0ClientSecret: string;
  readonly authorizationHost: string;
  /** Plaintext; empty on update means "keep the stored value". */
  readonly smartClientId: string;
  /** Plaintext; empty means "keep the stored value", or — with none stored — a public client. */
  readonly smartClientSecret: string;
  readonly smartRedirectUri: string;
  readonly smartScopes: string;
}

export interface UpdateFhirIntegrationInput extends FhirIntegrationInput {
  readonly id: string;
}

function blankToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function toDto(input: FhirIntegrationInput, id?: string): FhirIntegrationDto {
  const dto: FhirIntegrationDto = {
    ...(id !== undefined ? { id } : {}),
    name: input.name.trim(),
    is_default: input.isDefault,
    active: input.active,
    skip_authentication: input.skipAuthentication,
    authorization_criteria: input.authorizationCriteria,
    patient_id_header_name: blankToUndefined(input.patientIdHeaderName) ?? null,
    access_token_header_name: blankToUndefined(input.accessTokenHeaderName) ?? null,
    frontend_base_url: blankToUndefined(input.frontendBaseUrl) ?? null,
    patient_id_search_criteria: blankToUndefined(input.patientIdSearchCriteria) ?? null,
    patient_id_extension_url: blankToUndefined(input.patientIdExtensionUrl) ?? null,
    fhir_service_root_url: blankToUndefined(input.fhirServiceRootUrl) ?? null,
    auth0_token_url: blankToUndefined(input.auth0TokenUrl) ?? null,
    auth0_api_audience: blankToUndefined(input.auth0ApiAudience) ?? null,
    auth0_connection: blankToUndefined(input.auth0Connection) ?? null,
    authorization_host: blankToUndefined(input.authorizationHost) ?? null,
    smart_redirect_uri: blankToUndefined(input.smartRedirectUri) ?? null,
    smart_scopes: blankToUndefined(input.smartScopes) ?? null,
  };
  // Write-only secrets: only send them when non-blank, so a blank input keeps the stored value.
  const systemAccountUser = blankToUndefined(input.systemAccountUser);
  if (systemAccountUser !== undefined) dto.system_account_user = systemAccountUser;
  const systemAccountPass = blankToUndefined(input.systemAccountPass);
  if (systemAccountPass !== undefined) dto.system_account_pass = systemAccountPass;
  const auth0ClientId = blankToUndefined(input.auth0ClientId);
  if (auth0ClientId !== undefined) dto.auth0_client_id = auth0ClientId;
  const auth0ClientSecret = blankToUndefined(input.auth0ClientSecret);
  if (auth0ClientSecret !== undefined) dto.auth0_client_secret = auth0ClientSecret;
  const smartClientId = blankToUndefined(input.smartClientId);
  if (smartClientId !== undefined) dto.smart_client_id = smartClientId;
  const smartClientSecret = blankToUndefined(input.smartClientSecret);
  if (smartClientSecret !== undefined) dto.smart_client_secret = smartClientSecret;
  return dto;
}

export function useAdminFhirIntegrations(): UseQueryResult<FhirIntegrationViewModel[]> {
  return useQuery<FhirIntegrationViewModel[]>({
    queryKey: FHIR_INTEGRATION_ENTRIES_KEY,
    queryFn: async ({ signal }) => {
      const page = await adminFhirIntegrationEntryEndpoints.getFhirIntegrations({ size: 200, signal });
      return page.content.map(mapFhirIntegration);
    },
  });
}

/** A single entry, for the per-integration detail view (PTV2-1473). */
export function useAdminFhirIntegration(id: string): UseQueryResult<FhirIntegrationViewModel> {
  return useQuery<FhirIntegrationViewModel>({
    queryKey: [...FHIR_INTEGRATION_ENTRIES_KEY, id],
    queryFn: async ({ signal }) =>
      mapFhirIntegration(await adminFhirIntegrationEntryEndpoints.getFhirIntegration(id, { signal })),
  });
}

export function useAdminFhirIntegrationMutations(): {
  readonly create: UseMutationResult<FhirIntegrationViewModel, Error, FhirIntegrationInput>;
  readonly update: UseMutationResult<FhirIntegrationViewModel, Error, UpdateFhirIntegrationInput>;
  readonly remove: UseMutationResult<undefined, Error, string>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: FHIR_INTEGRATION_ENTRIES_KEY }).catch(() => undefined);
  };

  const create = useMutation<FhirIntegrationViewModel, Error, FhirIntegrationInput>({
    mutationFn: async (input) =>
      mapFhirIntegration(await adminFhirIntegrationEntryEndpoints.createFhirIntegration(toDto(input))),
    onSettled: invalidate,
  });

  const update = useMutation<FhirIntegrationViewModel, Error, UpdateFhirIntegrationInput>({
    mutationFn: async ({ id, ...rest }) =>
      mapFhirIntegration(await adminFhirIntegrationEntryEndpoints.updateFhirIntegration(id, toDto(rest, id))),
    onSettled: invalidate,
  });

  const remove = useMutation<undefined, Error, string>({
    mutationFn: (id) => adminFhirIntegrationEntryEndpoints.deleteFhirIntegration(id),
    onSettled: invalidate,
  });

  return { create, update, remove };
}
